import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { FaUserCircle } from "react-icons/fa";
import moment from "moment-hijri";
import { StudentsDoughnut } from "../../../components/dashboard/StudentsDoughnut";
import { AttendanceChart } from "../../../components/dashboard/AttendanceChart";
import { OneWeekCalendar } from "../../../components/dashboard/OneWeekCalendar";
import styles from "../css/dashboard.module.css";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS, { BASE_URL } from "../../../constants/apiConstants";
import { RoomsDropdown } from "../../../components/dashboard/RoomsDropdown";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useInboxContext } from "../../../context/InboxContext";
import {
  ADMIN_STUDENTS_ROUTE,
  ADMIN_STAFF_ROUTE,
  ADMIN_MESSAGES_ROUTE,
  ADMIN_STUDENT_ATTENDANCE_ROUTE,
} from "../../../routes/RoutesPathes";

export const Dashboard = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { newReceivedMessage, authorIsCurrentUser } = useInboxContext();
  const { auth, school } = useAuth();
  const NURSERY_ID = school.id;
  const USER_ID = auth.id;

  const gendersArray = [
    t("admin_pages.dashboard.males"),
    t("admin_pages.dashboard.females"),
    t("admin_pages.dashboard.unspecified"),
  ];

  const [studentsCount, setStudentsCount] = useState(null);
  const [staffCount, setStaffCount] = useState(null);
  const [newcomersCount, setNewcomersCount] = useState(null);
  const [genderDataset, setGenderDataset] = useState(null);
  const [attendanceDataset, setAttendanceDataset] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [birthdays, setBirthdays] = useState(null);
  const [workiversaries, setWorkiversaries] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [monthEvents, setMonthEvents] = useState(null);
  const [monthEventsLoading, setMonthEventsLoading] = useState(false);
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    const fetchStudentsCount = async () => {
      try {
        const api_url = API_URLS.getStudentsCount(NURSERY_ID);
        const response = await axiosPrivate.get(api_url);
        const data = response?.data;
        setStudentsCount(data?.total_active_students);
        const maleCount = data?.total_male;
        const femaleCount = data?.total_female;
        const unsetCount = data?.total_active_students - data?.total_students_gender;
        setGenderDataset([maleCount, femaleCount, unsetCount]);
      } catch (error) {
        // Do nothing
      }
    };

    fetchStudentsCount();
  }, [axiosPrivate, NURSERY_ID]);

  useEffect(() => {
    const fetchStaffCount = async () => {
      try {
        const api_url = API_URLS.getStaffCount(NURSERY_ID);
        const response = await axiosPrivate.get(api_url);
        setStaffCount(response?.data?.total_active_staff);
      } catch (error) {
        // Do nothing
      }
    };

    fetchStaffCount();
  }, [axiosPrivate, NURSERY_ID]);

  useEffect(() => {
    const fetchStaffCount = async () => {
      try {
        const api_url = API_URLS.getNewcomersCount(NURSERY_ID);
        const response = await axiosPrivate.get(api_url);
        setNewcomersCount(response?.data?.count);
      } catch (error) {
        // Do nothing
      }
    };

    fetchStaffCount();
  }, [axiosPrivate, NURSERY_ID]);

  useEffect(() => {
    const getStartAndEndOfWeek = () => {
      const today = new Date();

      let startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
      startOfWeek = startOfWeek.toISOString().split("T")[0]; // Format to YYYY-MM-DD

      let endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // Saturday
      endOfWeek = endOfWeek.toISOString().split("T")[0]; // Format to YYYY-MM-DD

      return { startOfWeek, endOfWeek };
    };

    const fetchAttendanceDataset = async () => {
      if (!selectedRoom) return;
      try {
        setAttendanceLoading(true);
        const { startOfWeek, endOfWeek } = getStartAndEndOfWeek();
        const roomId = selectedRoom.value;
        let api_url = '';
        if (roomId === 'all') {
          api_url = API_URLS.getGeneralAttendanceDataset(NURSERY_ID, startOfWeek, endOfWeek);
        } else {
          api_url = API_URLS.getAttendanceDataset(NURSERY_ID, roomId, startOfWeek, endOfWeek);
        }
        const response = await axiosPrivate.get(api_url);
        
        let label1 = response?.data?.datasets?.[0]?.label;
        label1 = label1?.toLowerCase();
        label1 = t(`admin_pages.dashboard.${label1}`);

        let label2 = response?.data?.datasets?.[1]?.label;
        label2 = label2?.toLowerCase();
        label2 = t(`admin_pages.dashboard.${label2}`);

        setAttendanceDataset({
          labels: response?.data?.days,
          dataset1: {
            label: label1,
            data: response?.data?.datasets?.[0]?.data,
          },
          dataset2: {
            label: label2,
            data: response?.data?.datasets?.[1]?.data,
          },
        });
      } catch (error) {
        // Do nothing
      } finally {
        setAttendanceLoading(false);
      }
    };

    fetchAttendanceDataset();
  }, [t, axiosPrivate, selectedRoom, NURSERY_ID]);

  useEffect(() => {
    const fetchBirthdays = async () => {
      try {
        const api_url = API_URLS.getBirthdays(NURSERY_ID);
        const response = await axiosPrivate.get(api_url);
        const students = response?.data;

        const groupedByDayMonth = students?.reduce((acc, student) => {
          const [year, month, day] = student.birth_date.split("-");
          
          // Group by Day Month
          const date = new Date(parseInt(year), parseInt(month) - 1)
          const monthName = date.toLocaleString('default', { month: 'long' });
          const key = `${day} ${monthName}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(student);
          return acc;
        }, {});

        setBirthdays(groupedByDayMonth);
      } catch (error) {
        // Do nothing
      }
    };

    fetchBirthdays();
  }, [axiosPrivate, NURSERY_ID]);

  useEffect(() => {
    const fetchWorkiversaries = async () => {
      try {
        const api_url = API_URLS.getWorkiversaries(NURSERY_ID);
        const response = await axiosPrivate.get(api_url);
        const staff = response?.data;

        const groupedWorkiversaries = staff?.reduce((acc, staffMember) => {
          const date = new Date(staffMember.EmpHireDate);
          const day = date.getDate();
          const monthName = date.toLocaleString("default", { month: "long" });
          const key = `${day} ${monthName}`; // "21 June"

          if (!acc[key]) {
            acc[key] = [];
          }

          acc[key].push(staffMember);
          return acc;
        }, {});

        setWorkiversaries(groupedWorkiversaries);
      } catch (error) {
        // Do nothing
      }
    };

    fetchWorkiversaries();
  }, [axiosPrivate, NURSERY_ID]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setEventsLoading(true);
        const date = selectedDate.format("YYYY-MM-DD");
        const api_url = API_URLS.getEvents(NURSERY_ID, date);
        const response = await axiosPrivate.get(api_url);
        setEvents(response?.data);
      } catch (error) {
        // Do nothing
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [axiosPrivate, selectedDate, NURSERY_ID]);

  useEffect(() => {
  const fetchMonthEvents = async () => {
    try {
      setMonthEventsLoading(true);
      const startOfMonth = selectedDate.clone().startOf('month').format('YYYY-MM-DD');
      const endOfMonth = selectedDate.clone().endOf('month').format('YYYY-MM-DD');
      const api_url = API_URLS.getMonthEvents(USER_ID,NURSERY_ID, startOfMonth, endOfMonth);
      const response = await axiosPrivate.get(api_url);
      setMonthEvents(response?.data);
    } catch (error) {
      // Do nothing
    } finally {
      setMonthEventsLoading(false);
    }
  };

    fetchMonthEvents();
  }, [axiosPrivate, selectedDate.format('YYYY-MM'), NURSERY_ID]);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const api_url = API_URLS.getLastFourMessages(NURSERY_ID);
        const response = await axiosPrivate.get(api_url);
        setMessages(response?.data);
      } catch (error) {
        // Do nothing
      }
    };

    fetchMessages();
  }, [axiosPrivate, NURSERY_ID]);

  // Effect to update messages when receiving new message
  useEffect(() => {
    if (newReceivedMessage) {
      const isCurrenUser = authorIsCurrentUser(newReceivedMessage.author);
      // Just update if the current user is not the current user
      if (! isCurrenUser) {
        setMessages(prev => [newReceivedMessage, ...prev.slice(0, 3)]);
      }
    }
  }, [newReceivedMessage, authorIsCurrentUser]);

  const getSenderTypeTranslation = (senderType) => {
    if (senderType.toLowerCase().startsWith("staff")) {
      return t(`admin_pages.dashboard.staff_member`)
    }
    return t(`admin_pages.dashboard.${senderType}`)
  };

  const navigateToStudents = (filterType, value) => {
    let route = ADMIN_STUDENTS_ROUTE;


    const params = new URLSearchParams();
    params.append('status', 'active');

    if (value && filterType === 'gender') {
      // Convert to lowercase and remove trailing 's' if present
      let genderParam = value.toLowerCase();
      
      // Remove trailing 's' to make singular (Males -> male, Females -> female)
      if (genderParam.endsWith('s')) {
        genderParam = genderParam.slice(0, -1);
      }

      params.append('gender', genderParam);
    } else if (filterType === 'newcomers' && value === true) {
      params.append('newcomers', 'true');
    }
    route += `?${params.toString()}`;
    navigate(route);
  };

  const navigateToStaff = () => {
    navigate(ADMIN_STAFF_ROUTE);
  };

  const navigateToMessages = (message) => {
    navigate(`${ADMIN_MESSAGES_ROUTE}#inbox=${message.group_type}&group=${message.group}`);
  };

  const navigateToAttendance = () => {
    const roomId = selectedRoom.value;
    if(!isNaN(roomId)) {
      navigate(`${ADMIN_STUDENT_ATTENDANCE_ROUTE}/?room=${roomId}`);
    } else {
      navigate(ADMIN_STUDENT_ATTENDANCE_ROUTE);
    }
  };

  const groupedMonthEvents = monthEvents?.reduce((acc, event) => {
  const key = moment(event.start_datetime).format('DD MMM');
  if (!acc[key]) acc[key] = [];
  acc[key].push(event);
  return acc;
}, {});

  return (
    <div>
      <Container fluid className="mt-4">
        <Row>
          {/* Left Column */}
          <Col md={12} lg={8} className="mb-4">
            {/* Row 1: 3 equal columns */}
            <Row className="mb-4">
              <Col md={4}>
                <Card
                  className={`s-bg-yellow text-white mx-1 mb-3 mb-md-0 unselectable hover-cursor-pointer ${styles.card}`}
                  onClick={() => navigateToStudents()}
                >
                  <Card.Body>
                    <div className="fs-3 fw-bold mb-0">
                      {studentsCount === null ? <Spinner size="sm" style={{borderWidth: "3px"}} /> : studentsCount.toLocaleString()}
                    </div>
                    <Card.Text className="small">{t("admin_pages.dashboard.students")}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card
                  className={`s-bg-fuchia text-white mx-1 mb-3 mb-md-0 unselectable hover-cursor-pointer ${styles.card}`}
                  onClick={() => navigateToStaff({})}
                >
                  <Card.Body>
                    <div className="fs-3 fw-bold mb-0">
                      {staffCount === null ? <Spinner size="sm" style={{borderWidth: "3px"}} /> : staffCount.toLocaleString()}
                    </div>
                    <Card.Text className="small">{t("admin_pages.dashboard.staff")}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card
                  className={`s-bg-yellow text-white mx-1 mb-3 mb-md-0 unselectable hover-cursor-pointer ${styles.card}`}
                  onClick={() => navigateToStudents('newcomers', true)}
                >
                  <Card.Body>
                    <div className="fs-3 fw-bold mb-0">
                      {newcomersCount === null ? <Spinner size="sm" style={{borderWidth: "3px"}} /> : newcomersCount.toLocaleString()}
                    </div>
                    <Card.Text className="small">{t("admin_pages.dashboard.newcomers")}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Row 2: 2 columns with ratio 5:7 */}
            <Row className="mb-4">
              <Col md={5} className="mb-4 mb-md-0">
                <Card className={`h-100 ${styles.card} ${styles.shadow}`}>
                  <Card.Body>
                    <Card.Title className="text-muted mb-0">
                      {t("admin_pages.dashboard.students")}
                    </Card.Title>
                    <div className="h-100 d-flex flex-column justify-content-center align-items-center">
                      {genderDataset?.every(val => val === 0) ? (
                          <p className="text-muted text-center my-5">{t("admin_pages.dashboard.no_students")}</p>
                        ) : genderDataset ? (
                          <StudentsDoughnut labels={gendersArray} dataset={genderDataset} onSegmentClick={navigateToStudents} />
                        ) : (
                          <Spinner size="md" style={{color: "#777"}} />
                        )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={7} className="mb-2 mb-md-0">
                <Card className={`h-100 mb-3 mb-md-0 ${styles.card} ${styles.shadow}`}>
                  <Card.Body>
                    <Card.Title className="text-muted d-flex justify-content-between">
                      <p>{t("admin_pages.dashboard.attendance")}</p>
                      <RoomsDropdown nurseryId={NURSERY_ID} selectedRoom={selectedRoom} setSelectedRoom={setSelectedRoom} />
                    </Card.Title>
                    {!selectedRoom ? (
                        <p className="text-muted text-center my-5">{t("admin_pages.dashboard.no_rooms")}</p>
                      ) : (
                      attendanceDataset === null || attendanceLoading ? (
                        <div className="h-75 d-flex flex-column justify-content-center align-items-center">
                          <Spinner size="md" style={{color: "#777"}} />
                        </div>
                    ) : (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <AttendanceChart chartData={attendanceDataset} onSegmentClick={navigateToAttendance} />
                        </div>
                      )
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Row 3: 2 equal columns */}
            <Row>
              <Col md={6} className="mb-4 mb-md-0">
                <Card className={`h-100 ${styles.card} ${styles.shadow}`}>
                  <Card.Body>
                    <Card.Title className="text-muted mb-3">
                      {t("admin_pages.dashboard.students_birthdays")}
                      <span className="fs-6 text-secondary"> ({t("admin_pages.dashboard.this_month")})</span>
                    </Card.Title>
                    {birthdays === null ? (
                      <div className="h-75 d-flex justify-content-center align-items-center">
                        <Spinner size="md" style={{color: "#777"}} />
                      </div>
                    ) : Object.keys(birthdays).length === 0 ? (
                      <div className={`h-75 d-flex flex-column justify-content-center align-items-center`}>
                        <p className="my-5 small text-center text-secondary">{t("admin_pages.dashboard.no_birthdays")}</p>
                      </div>
                    ) : (
                      <div className={`d-flex flex-column gap-2 ${styles.maxh15rem}`}>
                        {Object.entries(birthdays).map(
                          ([dateKey, dayStudents]) => {
                            return (
                              <div key={dateKey} className={styles.eventCard}>
                                <p>{dateKey}</p>
                                <div className={styles.verticalLine}></div>
                                <div>
                                  {dayStudents.map((student) => (
                                    <p 
                                      key={student.id}
                                      onClick={() => navigate(`${ADMIN_STUDENTS_ROUTE}/details/${student.id}`)}
                                    >
                                      {student.first_name} {student.last_name}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            );
                          })
                        }
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className={`h-100 ${styles.card} ${styles.shadow}`}>
                  <Card.Body>
                    <Card.Title className="text-muted mb-3">{t("admin_pages.dashboard.staff_workiversaries")}</Card.Title>
                    {workiversaries === null ? (
                      <div className="h-75 d-flex justify-content-center align-items-center">
                        <Spinner size="md" style={{color: "#777"}} />
                      </div>
                    ) : Object.keys(workiversaries).length === 0 ? (
                      <div className={`h-75 d-flex justify-content-center align-items-center`}>
                        <p className="my-5 small text-center text-secondary">{t("admin_pages.dashboard.no_workiversaries")}</p>
                      </div>
                    ) : (
                      <div className={`d-flex flex-column gap-2 ${styles.maxh15rem}`}>
                        {Object.entries(workiversaries).map(
                          ([dateKey, staff]) => {
                            return (
                              <div key={dateKey} className={styles.eventCard}>
                                <p>{dateKey}</p>
                                <div className={styles.verticalLine}></div>
                                <div>
                                  {staff.map((staffMember) => (
                                    <p 
                                      key={staffMember.id}
                                      onClick={() => navigate(`${ADMIN_STAFF_ROUTE}/details/${staffMember.id}`)}
                                    >
                                      {staffMember.FirstName}{" "}{staffMember.LastName}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>

          {/* Right Column */}
          <Col md={12} lg={4} className="mb-4">
            <Row className="mb-4">
              <Col>
                <Card className={`${styles.card} ${styles.shadow}`}>
                  <Card.Body>
                    <OneWeekCalendar selectedDate={selectedDate} onDateChange={setSelectedDate} />
                    <Card.Title className="text-muted my-3 mt-4">{t("admin_pages.dashboard.agenda")}</Card.Title>
                    {events === null || eventsLoading ? (
                      <div className={`d-flex justify-content-center align-items-center ${styles.h12rem}`}>
                        <Spinner size="md" style={{color: "#777"}} />
                      </div>
                    ) : events.length === 0 ? (
                      <div className={`d-flex flex-column justify-content-center align-items-center ${styles.h12rem}`}>
                        <p className="my-5 small text-center text-secondary">{t("admin_pages.dashboard.no_events")}</p>
                      </div>
                    ) : (
                      <div className={`d-flex flex-column gap-2 ${styles.h12rem}`}>
                        {events.map((e) => (
                          <div key={e.id} className={styles.eventCard}>
                            <span className="text-secondary small">{e?.room?.ClassName}</span>
                            <span>&nbsp; {e.title}</span>
                          </div>
                        ))}
                      </div>
                    )}

                     <Card.Title className="text-muted my-3 mt-4">
                      {t("admin_pages.dashboard.upcoming_events")} 
                    </Card.Title>
                    {monthEvents === null || monthEventsLoading ? (
                      <div className={`d-flex justify-content-center align-items-center ${styles.h12rem}`}>
                        <Spinner size="md" style={{color: "#777"}} />
                      </div>
                    ) : monthEvents.length === 0 ? (
                      <div className={`d-flex flex-column justify-content-center align-items-center ${styles.h12rem}`}>
                        <p className="my-5 small text-center text-secondary">{t("admin_pages.dashboard.no_events")}</p>
                      </div>
                    ) : (
                      <div className={`d-flex flex-column gap-2 ${styles.h12rem}`}>
                        {Object.entries(
                          monthEvents.reduce((acc, event) => {
                            const key = moment(event.start_datetime).format('DD MMM');
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(event);
                            return acc;
                          }, {})
                        ).map(([dateKey, dayEvents]) => (
                          <div key={dateKey} className={`${styles.eventCard} ${styles.notClickable}`}>
                            <p>{dateKey}</p>
                            <div className={styles.verticalLine}></div>
                            <div>
                              {dayEvents.map((event) => (
                                <p key={event.id}>
                                  <span className="text-secondary small">{event?.room?.ClassName}</span>
                                  <span>&nbsp; {event.title}</span>
                                </p>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col>
                <Card className={`${styles.card} ${styles.shadow}`}>
                  <Card.Body>
                    <Card.Title className="text-muted mb-3">{t("admin_pages.dashboard.messages")}</Card.Title>
                    <div className="d-flex flex-column gap-2">
                      {messages === null ? (
                        <div className="h-75 d-flex justify-content-center align-items-center">
                          <Spinner size="md" style={{color: "#777"}} />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className={`h-75 d-flex flex-column justify-content-center align-items-center`}>
                          <p className="my-5 small text-center text-secondary">{t("admin_pages.dashboard.no_messages")}</p>
                        </div>
                      ) : (
                        messages.map((message) => (
                          <div key={message.id} className={styles.messageCard} onClick={() => navigateToMessages(message)}>
                            {!message.author?.image ? (
                              <FaUserCircle color="#999" className={styles.imgMsg} />
                            ) : (
                              <img src={`${BASE_URL}${message.author?.image}`} alt="" className={styles.imgMsg} />
                            )}
                            <div>
                              <p className="m-0 small d-flex align-items-center">
                                <span>{message.author?.first_name} {message.author?.last_name}</span>
                                <span className={styles.senderRole}>
                                  &nbsp;&nbsp;&nbsp;{getSenderTypeTranslation(message.author?.type)}
                                </span>
                              </p>
                              
                              <p className={`m-0 ${styles.textMsg}`}>{message.body}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
